import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { SoccerBall, Trophy, User as UserIcon, Users, Plus, PencilSimple, Trash, X, ChartBar } from "@phosphor-icons/react";

const TABS = [
  { id: "marcadores", label: "Marcadores", icon: SoccerBall },
  { id: "ligas",      label: "Ligas",      icon: Trophy },
  { id: "equipos",    label: "Equipos",    icon: Users },
  { id: "jugadores",  label: "Jugadores",  icon: UserIcon },
];

const POSITIONS = ["portero", "defensa", "medio", "delantero"];
const STATUSES = ["programado", "en_curso", "finalizado", "cancelado"];

const STATUS_STYLES = {
  programado: "border-blue-500/30 text-blue-300",
  en_curso: "border-amber-500/30 text-amber-300",
  finalizado: "border-[#CCFF00]/30 text-[#CCFF00]",
  cancelado: "border-red-500/30 text-red-300",
};

export default function Balonpie() {
  const { user } = useAuth();
  const [tab, setTab] = useState("marcadores");
  const isStaff = user && (user.role === "admin" || user.role === "directivo");
  return (
    <div className="space-y-6" data-testid="balonpie-page">
      <div>
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] font-bold">Módulo · Balonpié</p>
        <h1 className="font-['Barlow_Condensed'] text-4xl font-black uppercase tracking-tighter">Fútbol · DYD</h1>
        <p className="text-xs text-gray-500 mt-1">Marcadores, ligas, equipos y plantillas. Base inicial — se expande con goleadores/tarjetas/alineaciones más adelante.</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-3" data-testid="balonpie-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const on = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold border transition-colors ${on ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
              data-testid={`balonpie-tab-${t.id}`}
            >
              <Icon size={14} weight={on ? "fill" : "regular"} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "marcadores" && <MatchesTab isStaff={isStaff} />}
      {tab === "ligas"      && <LeaguesTab isStaff={isStaff} />}
      {tab === "equipos"    && <TeamsTab isStaff={isStaff} />}
      {tab === "jugadores"  && <PlayersTab isStaff={isStaff} />}
    </div>
  );
}

/* ==================== TEAMS ==================== */
function TeamsTab({ isStaff }) {
  const [teams, setTeams] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ name: "", club_id: "", crest_url: "", primary_color: "#CCFF00", secondary_color: "#000000" });

  const load = () => api.get("/football/teams").then(({ data }) => setTeams(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ name: "", club_id: "", crest_url: "", primary_color: "#CCFF00", secondary_color: "#000000" }); setDialog("create"); };
  const openEdit = (t) => { setForm({ name: t.name, club_id: t.club_id || "", crest_url: t.crest_url || "", primary_color: t.primary_color || "#CCFF00", secondary_color: t.secondary_color || "#000000" }); setDialog({ mode: "edit", team: t }); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      if (dialog === "create") await api.post("/football/teams", form);
      else await api.put(`/football/teams/${dialog.team.id}`, form);
      toast.success(dialog === "create" ? "Equipo creado" : "Equipo actualizado");
      setDialog(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar equipo "${t.name}"?`)) return;
    try { await api.delete(`/football/teams/${t.id}`); toast.success("Eliminado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="balonpie-teams">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{teams.length} equipos registrados</p>
        {isStaff && (
          <Button onClick={openCreate} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs h-9" data-testid="team-new">
            <Plus size={14} className="mr-1" /> Nuevo equipo
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {teams.length === 0 && <p className="text-xs text-gray-500 italic col-span-full">Sin equipos aún.</p>}
        {teams.map((t) => (
          <div key={t.id} className="card-tactical p-4 border-white/10" data-testid={`team-${t.id}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full border-2 flex items-center justify-center overflow-hidden shrink-0" style={{ backgroundColor: t.primary_color, borderColor: t.secondary_color, color: t.secondary_color }}>
                {t.crest_url ? <img src={t.crest_url} alt={t.name} className="w-full h-full object-cover" /> : <span className="font-['Barlow_Condensed'] font-black text-lg">{t.name.slice(0, 2).toUpperCase()}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-['Barlow_Condensed'] font-black text-lg uppercase text-white truncate">{t.name}</h3>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="w-3 h-3 rounded-full border border-white/20" style={{ backgroundColor: t.primary_color }} />
                  <span className="w-3 h-3 rounded-full border border-white/20" style={{ backgroundColor: t.secondary_color }} />
                </div>
              </div>
            </div>
            {isStaff && (
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(t)} className="h-7 px-2 text-[10px] uppercase text-[#CCFF00]" data-testid={`team-edit-${t.id}`}><PencilSimple size={11} className="mr-1" /> Editar</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(t)} className="h-7 px-2 text-[10px] uppercase text-red-400 ml-auto" data-testid={`team-del-${t.id}`}><Trash size={11} /></Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-md" data-testid="team-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{dialog === "create" ? "Nuevo equipo" : "Editar equipo"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase mb-1 block">Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="team-form-name" /></div>
            <div><Label className="text-xs uppercase mb-1 block">Escudo (URL)</Label><Input value={form.crest_url} onChange={(e) => setForm({ ...form, crest_url: e.target.value })} placeholder="https://…" className="bg-[#0A0A0A] border-white/20 text-white h-10 text-xs" data-testid="team-form-crest" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase mb-1 block">Color primario</Label>
                <Input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-10 border-white/20 p-0" data-testid="team-form-primary" />
              </div>
              <div>
                <Label className="text-xs uppercase mb-1 block">Color secundario</Label>
                <Input type="color" value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="h-10 border-white/20 p-0" data-testid="team-form-secondary" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}><X size={14} className="mr-1" /> Cancelar</Button>
            <Button onClick={submit} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="team-form-submit">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ==================== PLAYERS ==================== */
function PlayersTab({ isStaff }) {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filter, setFilter] = useState("all");
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ name: "", team_id: "", jersey_number: "", position: "", birthdate: "" });

  const load = () => Promise.all([
    api.get("/football/players"),
    api.get("/football/teams"),
  ]).then(([p, t]) => { setPlayers(p.data); setTeams(t.data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const teamName = (id) => teams.find((x) => x.id === id)?.name || "Sin equipo";
  const visible = useMemo(() => filter === "all" ? players : players.filter((p) => p.team_id === filter), [players, filter]);

  const openCreate = () => { setForm({ name: "", team_id: filter !== "all" ? filter : "", jersey_number: "", position: "", birthdate: "" }); setDialog("create"); };
  const openEdit = (p) => { setForm({ name: p.name, team_id: p.team_id || "", jersey_number: p.jersey_number ?? "", position: p.position || "", birthdate: p.birthdate || "" }); setDialog({ mode: "edit", player: p }); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    const payload = {
      name: form.name.trim(),
      team_id: form.team_id || null,
      position: form.position || null,
      birthdate: form.birthdate || null,
      jersey_number: form.jersey_number === "" ? null : parseInt(form.jersey_number),
    };
    try {
      if (dialog === "create") await api.post("/football/players", payload);
      else await api.put(`/football/players/${dialog.player.id}`, payload);
      toast.success(dialog === "create" ? "Jugador agregado" : "Actualizado");
      setDialog(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    try { await api.delete(`/football/players/${p.id}`); toast.success("Eliminado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="balonpie-players">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-9 text-xs max-w-xs" data-testid="players-filter"><SelectValue placeholder="Filtrar por equipo…" /></SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-white/10">
            <SelectItem value="all">Todos los equipos</SelectItem>
            {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {isStaff && (
          <Button onClick={openCreate} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs h-9" data-testid="player-new">
            <Plus size={14} className="mr-1" /> Nuevo jugador
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {visible.length === 0 && <p className="text-xs text-gray-500 italic col-span-full">Sin jugadores.</p>}
        {visible.map((p) => (
          <div key={p.id} className="card-tactical p-3 border-white/10 flex items-center gap-3" data-testid={`player-${p.id}`}>
            <div className="w-10 h-10 shrink-0 rounded bg-black/60 border border-white/10 flex items-center justify-center font-['Barlow_Condensed'] text-lg font-black text-[#CCFF00]">
              {p.jersey_number ?? "#"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{p.name}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-widest truncate">{teamName(p.team_id)} · {p.position || "s/pos"}</p>
            </div>
            {isStaff && (
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="h-6 px-1.5 text-[10px] text-[#CCFF00]" data-testid={`player-edit-${p.id}`}><PencilSimple size={10} /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(p)} className="h-6 px-1.5 text-[10px] text-red-400" data-testid={`player-del-${p.id}`}><Trash size={10} /></Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-md" data-testid="player-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{dialog === "create" ? "Nuevo jugador" : "Editar jugador"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase mb-1 block">Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="player-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase mb-1 block">Dorsal</Label><Input type="number" value={form.jersey_number} onChange={(e) => setForm({ ...form, jersey_number: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="player-form-jersey" /></div>
              <div>
                <Label className="text-xs uppercase mb-1 block">Posición</Label>
                <Select value={form.position || "__none"} onValueChange={(v) => setForm({ ...form, position: v === "__none" ? "" : v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10 text-xs" data-testid="player-form-position"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="__none">—</SelectItem>
                    {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase mb-1 block">Equipo</Label>
              <Select value={form.team_id || "__none"} onValueChange={(v) => setForm({ ...form, team_id: v === "__none" ? "" : v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10 text-xs" data-testid="player-form-team"><SelectValue placeholder="Sin equipo" /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="__none">Sin equipo</SelectItem>
                  {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase mb-1 block">Fecha de nacimiento</Label><Input type="date" value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="player-form-birthdate" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}><X size={14} className="mr-1" /> Cancelar</Button>
            <Button onClick={submit} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="player-form-submit">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ==================== LEAGUES ==================== */
function LeaguesTab({ isStaff }) {
  const [leagues, setLeagues] = useState([]);
  const [standings, setStandings] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ name: "", season: "", category: "", start_date: "", end_date: "", description: "" });

  const load = () => api.get("/football/leagues").then(({ data }) => setLeagues(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ name: "", season: "", category: "", start_date: "", end_date: "", description: "" }); setDialog("create"); };
  const openEdit = (l) => { setForm({ name: l.name, season: l.season || "", category: l.category || "", start_date: l.start_date || "", end_date: l.end_date || "", description: l.description || "" }); setDialog({ mode: "edit", league: l }); };
  const openStandings = async (l) => {
    try { const { data } = await api.get(`/football/leagues/${l.id}/standings`); setStandings(data); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      if (dialog === "create") await api.post("/football/leagues", form);
      else await api.put(`/football/leagues/${dialog.league.id}`, form);
      toast.success(dialog === "create" ? "Liga creada" : "Actualizada");
      setDialog(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const remove = async (l) => {
    if (!window.confirm(`¿Eliminar liga "${l.name}"?`)) return;
    try { await api.delete(`/football/leagues/${l.id}`); toast.success("Eliminada"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="balonpie-leagues">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{leagues.length} ligas</p>
        {isStaff && <Button onClick={openCreate} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs h-9" data-testid="league-new"><Plus size={14} className="mr-1" /> Nueva liga</Button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {leagues.length === 0 && <p className="text-xs text-gray-500 italic col-span-full">Sin ligas aún.</p>}
        {leagues.map((l) => (
          <div key={l.id} className="card-tactical p-4 border-white/10" data-testid={`league-${l.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-['Barlow_Condensed'] font-black text-xl uppercase text-white truncate">{l.name}</h3>
                <p className="text-[10px] text-gray-500 uppercase tracking-widest">{l.season || ""} {l.category ? ` · ${l.category}` : ""}</p>
              </div>
              {isStaff && (
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(l)} className="h-7 px-2 text-[10px] text-[#CCFF00]" data-testid={`league-edit-${l.id}`}><PencilSimple size={11} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(l)} className="h-7 px-2 text-[10px] text-red-400" data-testid={`league-del-${l.id}`}><Trash size={11} /></Button>
                </div>
              )}
            </div>
            {l.description && <p className="text-xs text-gray-400 mt-2 line-clamp-2">{l.description}</p>}
            <div className="mt-3">
              <Button size="sm" variant="outline" onClick={() => openStandings(l)} className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 text-[10px] uppercase h-7" data-testid={`league-standings-${l.id}`}><ChartBar size={11} className="mr-1" /> Tabla</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-md" data-testid="league-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{dialog === "create" ? "Nueva liga" : "Editar liga"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase mb-1 block">Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="league-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase mb-1 block">Temporada</Label><Input value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} placeholder="2025-2026" className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="league-form-season" /></div>
              <div><Label className="text-xs uppercase mb-1 block">Categoría</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Libre / Sub-15 …" className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="league-form-category" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase mb-1 block">Inicio</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10" /></div>
              <div><Label className="text-xs uppercase mb-1 block">Fin</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10" /></div>
            </div>
            <div><Label className="text-xs uppercase mb-1 block">Descripción</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}><X size={14} className="mr-1" /> Cancelar</Button>
            <Button onClick={submit} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="league-form-submit">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Standings viewer */}
      <Dialog open={!!standings} onOpenChange={(v) => !v && setStandings(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-2xl" data-testid="league-standings-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{standings?.league?.name} · Tabla</DialogTitle></DialogHeader>
          {standings && (
            <div className="max-h-[60vh] overflow-y-auto">
              {standings.standings.length === 0 ? (
                <p className="text-xs text-gray-500 py-4">Sin partidos finalizados aún.</p>
              ) : (
                <div className="grid grid-cols-[24px_1fr_40px_40px_40px_40px_50px_40px] gap-2 text-[9px] uppercase tracking-widest text-gray-500 pb-2 border-b border-white/10">
                  <span>#</span><span>Equipo</span><span className="text-center">PJ</span><span className="text-center">G</span><span className="text-center">E</span><span className="text-center">P</span><span className="text-center">GF-GC</span><span className="text-center text-[#CCFF00]">Pts</span>
                </div>
              )}
              {standings.standings.map((row, i) => (
                <div key={row.team_id} className="grid grid-cols-[24px_1fr_40px_40px_40px_40px_50px_40px] gap-2 items-center py-2 text-xs border-b border-white/5" data-testid={`standing-row-${i}`}>
                  <span className="text-center font-bold" style={{ color: i < 3 ? "#CCFF00" : "#6B7280" }}>{i + 1}</span>
                  <span className="truncate text-white">{row.team_name || row.team_id}</span>
                  <span className="text-center text-gray-400">{row.played}</span>
                  <span className="text-center text-gray-400">{row.wins}</span>
                  <span className="text-center text-gray-400">{row.draws}</span>
                  <span className="text-center text-gray-400">{row.losses}</span>
                  <span className="text-center text-gray-400 text-[11px]">{row.goals_for}-{row.goals_against}</span>
                  <span className="text-center font-bold text-[#CCFF00]">{row.points}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ==================== MATCHES ==================== */
function MatchesTab({ isStaff }) {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ home_team_id: "", away_team_id: "", home_score: 0, away_score: 0, date: "", time: "", league_id: "", status: "programado" });

  const load = () => Promise.all([
    api.get("/football/matches"),
    api.get("/football/teams"),
    api.get("/football/leagues"),
  ]).then(([m, t, l]) => { setMatches(m.data); setTeams(t.data); setLeagues(l.data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const teamName = (id) => teams.find((x) => x.id === id)?.name || "—";
  const teamsById = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);

  const openCreate = () => { setForm({ home_team_id: teams[0]?.id || "", away_team_id: teams[1]?.id || "", home_score: 0, away_score: 0, date: new Date().toISOString().slice(0, 10), time: "", league_id: "", status: "programado" }); setDialog("create"); };
  const openEdit = (m) => { setForm({ home_team_id: m.home_team_id, away_team_id: m.away_team_id, home_score: m.home_score, away_score: m.away_score, date: m.date, time: m.time || "", league_id: m.league_id || "", status: m.status }); setDialog({ mode: "edit", match: m }); };

  const submit = async () => {
    if (!form.home_team_id || !form.away_team_id) { toast.error("Elige ambos equipos"); return; }
    if (form.home_team_id === form.away_team_id) { toast.error("Los equipos deben ser distintos"); return; }
    if (!form.date) { toast.error("Fecha requerida"); return; }
    const payload = {
      ...form,
      league_id: form.league_id || null,
      home_score: parseInt(form.home_score) || 0,
      away_score: parseInt(form.away_score) || 0,
    };
    try {
      if (dialog === "create") await api.post("/football/matches", payload);
      else await api.put(`/football/matches/${dialog.match.id}`, payload);
      toast.success(dialog === "create" ? "Partido creado" : "Actualizado");
      setDialog(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const remove = async (m) => {
    if (!window.confirm("¿Eliminar partido?")) return;
    try { await api.delete(`/football/matches/${m.id}`); toast.success("Eliminado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="balonpie-matches">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{matches.length} partidos</p>
        {isStaff && <Button onClick={openCreate} disabled={teams.length < 2} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs h-9" data-testid="match-new"><Plus size={14} className="mr-1" /> Nuevo partido</Button>}
      </div>
      {teams.length < 2 && <p className="text-[11px] text-amber-300 border border-amber-500/25 bg-amber-500/5 rounded p-3">Registra al menos 2 equipos en el tab "Equipos" antes de crear partidos.</p>}
      <div className="space-y-2">
        {matches.length === 0 && <p className="text-xs text-gray-500 italic">Sin partidos aún.</p>}
        {matches.map((m) => {
          const h = teamsById[m.home_team_id]; const a = teamsById[m.away_team_id];
          return (
            <div key={m.id} className="card-tactical p-4 border-white/10 flex items-center gap-3 flex-wrap" data-testid={`match-${m.id}`}>
              <div className="text-[10px] uppercase text-gray-500 w-24 shrink-0">
                {m.date} {m.time && <><br />{m.time}</>}
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-[10px] font-bold overflow-hidden" style={{ backgroundColor: h?.primary_color || "#333", color: h?.secondary_color || "#FFF" }}>
                  {h?.crest_url ? <img src={h.crest_url} alt={h.name} className="w-full h-full object-cover" /> : (h?.name || "?").slice(0, 2)}
                </div>
                <span className="text-sm text-white truncate">{teamName(m.home_team_id)}</span>
              </div>
              <div className="font-['Barlow_Condensed'] font-black text-2xl text-[#CCFF00] px-3">
                {m.home_score} <span className="text-gray-500">-</span> {m.away_score}
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                <span className="text-sm text-white truncate text-right">{teamName(m.away_team_id)}</span>
                <div className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-[10px] font-bold overflow-hidden" style={{ backgroundColor: a?.primary_color || "#333", color: a?.secondary_color || "#FFF" }}>
                  {a?.crest_url ? <img src={a.crest_url} alt={a.name} className="w-full h-full object-cover" /> : (a?.name || "?").slice(0, 2)}
                </div>
              </div>
              <Badge variant="outline" className={`text-[9px] uppercase h-5 ${STATUS_STYLES[m.status] || "border-white/10 text-gray-400"}`} data-testid={`match-status-${m.id}`}>{m.status}</Badge>
              {isStaff && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(m)} className="h-7 px-2 text-[10px] text-[#CCFF00]" data-testid={`match-edit-${m.id}`}><PencilSimple size={11} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(m)} className="h-7 px-2 text-[10px] text-red-400" data-testid={`match-del-${m.id}`}><Trash size={11} /></Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-lg" data-testid="match-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{dialog === "create" ? "Nuevo partido" : "Editar partido"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase mb-1 block">Local</Label>
                <Select value={form.home_team_id} onValueChange={(v) => setForm({ ...form, home_team_id: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10 text-xs" data-testid="match-form-home"><SelectValue placeholder="Equipo" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase mb-1 block">Visitante</Label>
                <Select value={form.away_team_id} onValueChange={(v) => setForm({ ...form, away_team_id: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10 text-xs" data-testid="match-form-away"><SelectValue placeholder="Equipo" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase mb-1 block">Goles local</Label><Input type="number" min="0" value={form.home_score} onChange={(e) => setForm({ ...form, home_score: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="match-form-home-score" /></div>
              <div><Label className="text-xs uppercase mb-1 block">Goles visitante</Label><Input type="number" min="0" value={form.away_score} onChange={(e) => setForm({ ...form, away_score: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="match-form-away-score" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase mb-1 block">Fecha</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="match-form-date" /></div>
              <div><Label className="text-xs uppercase mb-1 block">Hora</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase mb-1 block">Liga</Label>
                <Select value={form.league_id || "__none"} onValueChange={(v) => setForm({ ...form, league_id: v === "__none" ? "" : v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10 text-xs" data-testid="match-form-league"><SelectValue placeholder="Amistoso" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="__none">Amistoso (sin liga)</SelectItem>
                    {leagues.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase mb-1 block">Estado</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10 text-xs" data-testid="match-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}><X size={14} className="mr-1" /> Cancelar</Button>
            <Button onClick={submit} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="match-form-submit">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
